import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";
import Seo from "@/components/Seo";
import { ArrowRight, Sparkles, Leaf, BookOpen } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/7e6ca0f8-68b2-4411-b9b5-f4664e141e03/images/d9180094995df78a87906bb3ecb7843c90b7af767d20475bf2fa35b5dfa9628f.png";
const GUIDE_IMG = "https://static.prod-images.emergentagent.com/jobs/7e6ca0f8-68b2-4411-b9b5-f4664e141e03/images/c5e15c165982a14d36420e32a936a4cbda16b3bb8b3fbc098e58d763e7eb5972.png";

export default function HomePage() {
  const [recipes, setRecipes] = useState([]);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [rRes, gRes] = await Promise.all([
          api.get("/recipes/featured?limit=7"),
          api.get("/guides?limit=4"),
        ]);
        setRecipes(rRes.data.items || []);
        setGuides(gRes.data.items || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const featured = recipes[0];
  const grid = recipes.slice(1, 7);

  return (
    <>
      <Seo
        title="Recettes IG bas pour diabétiques"
        description="Recettes à index glycémique bas pour personnes diabétiques. Plats du monde adaptés, desserts sans sucre, guides nutrition pour diabétiques type 1 et type 2."
        keywords={["recettes diabète", "recettes IG bas", "cuisine diabétique", "index glycémique bas", "diabète type 2"]}
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Recette-Diabete",
          url: "https://recette-diabete.com",
          description: "Recettes à index glycémique bas pour personnes diabétiques.",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://recette-diabete.com/recettes?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, hsla(24,60%,94%,0.6) 0%, hsla(24,60%,94%,0.95) 100%)" }} />
        </div>
        <div className="container-x relative py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-8 items-end fade-up">
          <div className="md:col-span-8">
            <p className="overline mb-5">Recettes · Index glycémique bas</p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[1.02] tracking-tight">
              Les recettes que vous aimez,<br />
              <em className="text-[hsl(var(--brand-primary))] font-medium">repensées</em> pour les diabétiques.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[hsl(var(--text-secondary))] max-w-2xl leading-relaxed">
              Vos plats préférés — du terroir français aux saveurs du monde — adaptés en index glycémique bas,
              sans sacrifier le goût. Et des guides pour comprendre la maladie en toute sérénité.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/recettes" className="btn-primary" data-testid="hero-cta-recipes">
                Découvrir les recettes <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link to="/guides" className="btn-outline" data-testid="hero-cta-guides">
                Lire les guides
              </Link>
            </div>
          </div>
          <div className="md:col-span-4 hidden md:block">
            <div className="border-l border-[hsl(var(--border-default))] pl-6">
              <p className="overline mb-3">Notre engagement</p>
              <p className="font-serif text-2xl leading-snug text-[hsl(var(--text-primary))]">
                Chaque recette est sélectionnée et adaptée pour respecter un index glycémique bas, sans rien céder sur le plaisir.
              </p>
              <div className="mt-4 flex items-center gap-2 text-xs text-[hsl(var(--text-muted))]">
                <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--brand-accent))]" strokeWidth={1.5} />
                <span>Validées par notre charte nutrition diabétique</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED + GRID */}
      <section className="container-x py-20" data-testid="featured-section">
        <div className="flex items-baseline justify-between mb-12">
          <div>
            <p className="overline mb-2">À la une</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Recettes du moment</h2>
          </div>
          <Link to="/recettes" className="hidden md:inline-flex items-center gap-1 text-sm text-[hsl(var(--brand-primary))] hover:underline" data-testid="see-all-recipes">
            Toutes les recettes <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>

        {loading ? (
          <div className="text-[hsl(var(--text-muted))] py-12">Chargement…</div>
        ) : recipes.length === 0 ? (
          <div className="border border-dashed border-[hsl(var(--border-default))] rounded-sm p-12 text-center" data-testid="empty-recipes">
            <p className="font-serif text-2xl mb-2">Les premières recettes arrivent bientôt.</p>
            <p className="text-sm text-[hsl(var(--text-muted))]">Revenez d'ici peu — nous publions régulièrement de nouvelles recettes à index glycémique bas.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {featured && (
              <div className="md:col-span-7">
                <RecipeCard recipe={featured} featured />
              </div>
            )}
            <div className="md:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {grid.slice(0, 4).map((r) => (
                <RecipeCard key={r.id} recipe={r} />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* CATEGORIES */}
      <section className="container-x py-12" data-testid="categories-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { slug: "entree", label: "Entrées", desc: "Veloutés, terrines, salades", icon: Leaf },
            { slug: "plat", label: "Plats", desc: "Mijotés, gratins, traditions", icon: Sparkles },
            { slug: "dessert", label: "Desserts", desc: "Sans sucre raffiné", icon: BookOpen },
          ].map((c) => (
            <Link
              key={c.slug}
              to={`/categories/${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className="group border border-[hsl(var(--border-default))] p-8 rounded-sm bg-white hover:border-[hsl(var(--brand-primary))] transition-colors"
            >
              <c.icon className="w-6 h-6 mb-4 text-[hsl(var(--brand-primary))]" strokeWidth={1.5} />
              <p className="overline mb-2">Catégorie</p>
              <h3 className="font-serif text-3xl mb-1 group-hover:text-[hsl(var(--brand-primary))] transition-colors">{c.label}</h3>
              <p className="text-sm text-[hsl(var(--text-secondary))]">{c.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* GUIDES */}
      <section className="container-x py-20" data-testid="guides-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4 md:sticky md:top-24">
            <img src={GUIDE_IMG} alt="" className="w-full aspect-[4/5] object-cover rounded-sm mb-6" />
            <p className="overline mb-2">Pour bien comprendre</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-none">Guides diabète</h2>
            <p className="mt-4 text-[hsl(var(--text-secondary))]">
              Vous venez d'apprendre que vous êtes diabétique ? Nos guides éditoriaux vous accompagnent,
              sans jargon, dans cette nouvelle étape.
            </p>
            <Link to="/guides" className="mt-6 inline-flex items-center gap-1 text-sm text-[hsl(var(--brand-primary))] hover:underline" data-testid="see-all-guides">
              Tous les guides <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {guides.map((g) => (
              <Link
                key={g.id}
                to={`/guides/${g.slug}`}
                className="block border-b border-[hsl(var(--border-light))] pb-6 group"
                data-testid={`guide-card-${g.slug}`}
              >
                <p className="overline mb-2">{g.category}</p>
                <h3 className="font-serif text-2xl leading-tight group-hover:text-[hsl(var(--brand-primary))] transition-colors">{g.title}</h3>
                <p className="mt-2 text-sm text-[hsl(var(--text-secondary))] line-clamp-3">{g.excerpt}</p>
                <p className="mt-3 text-xs text-[hsl(var(--text-muted))]">{g.reading_time_min} min de lecture</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
